// src/components/PublicNavbar.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { db } from "../firebaseConfig";
import {
  collection,
  getDocs,
  limit as fsLimit,
  orderBy,
  query,
  where,
  startAt,
  endAt,
} from "firebase/firestore";

/** Normalisation de base */
function normalize(str = "") {
  return str
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\p{L}\p{N}\s.'-]/gu, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

/** Vocabulaire simple prêt-à-porter */
const COLORS = [
  "noir",
  "blanc",
  "gris",
  "rouge",
  "bleu",
  "vert",
  "jaune",
  "marron",
  "beige",
  "rose",
  "orange",
  "violet",
  "kaki",
  "bordeaux",
  "marine",
  "camel",
  "taupe",
  "ivoire",
];
const SIZES_TEXT = ["xs", "s", "m", "l", "xl", "xxl", "xxxl"];
const SIZE_NUM_RANGE = { min: 30, max: 54 };
const SHOES_RANGE = { min: 35, max: 47 };

function parseSmartQuery(raw = "") {
  const q = normalize(raw);
  if (!q) return { search: "", params: new URLSearchParams() };

  const tokens = q.split(" ");
  const params = new URLSearchParams();
  const rest = [];

  let color = "";
  let size = "";
  let priceMin = "";
  let priceMax = "";

  for (const t of tokens) {
    if (COLORS.includes(t)) {
      color = t;
      continue;
    }
    if (SIZES_TEXT.includes(t)) {
      size = t.toUpperCase();
      continue;
    }

    if (/^\d{2}$/.test(t)) {
      const n = +t;
      if (n >= SHOES_RANGE.min && n <= SHOES_RANGE.max && !size) {
        size = String(n);
        continue;
      }
      if (n >= SIZE_NUM_RANGE.min && n <= SIZE_NUM_RANGE.max && !size) {
        size = String(n);
        continue;
      }
    }

    if (/^(<=|>=|<|>)\d{3,}$/.test(t)) {
      const m = t.match(/^(<=|>=|<|>)(\d+)$/);
      const op = m?.[1];
      const num = m?.[2];
      if (op && num) {
        if (op === "<" || op === "<=") priceMax = num;
        if (op === ">" || op === ">=") priceMin = num;
        continue;
      }
    }

    if (/^\d{1,3}k$/.test(t)) {
      const n = parseInt(t, 10) * 1000;
      if (!priceMin) priceMin = String(n);
      else priceMax = String(n);
      continue;
    }

    rest.push(t);
  }

  const search = rest.join(" ");
  if (search) params.set("search", search);
  if (color) params.set("color", color);
  if (size) params.set("size", size);
  if (priceMin) params.set("minPrice", priceMin);
  if (priceMax) params.set("maxPrice", priceMax);

  return { search, params };
}

function uniqueByRefId(arr) {
  const seen = new Set();
  const out = [];
  for (const it of arr) {
    const key = it.refId || `${it.type || "x"}:${it.url || it.id || ""}`;
    if (!seen.has(key)) {
      seen.add(key);
      out.push(it);
    }
  }
  return out;
}

export default function PublicNavbar() {
  const { items } = useCart();
  const countCart = useMemo(
    () => items.reduce((s, it) => s + (Number(it.qty) || 0), 0),
    [items]
  );

  const [qText, setQText] = useState("");
  const [openLeft, setOpenLeft] = useState(false);

  const [suggests, setSuggests] = useState([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const [loadingSuggest, setLoadingSuggest] = useState(false);

  const inputRef = useRef(null);
  const suggestBoxRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();

  // Pré-remplir search depuis URL
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    setQText(sp.get("search") || "");
  }, [location.search]);

  // Fermer drawer & suggest lors d'un changement de route
  useEffect(() => {
    setOpenLeft(false);
    setShowSuggest(false);
  }, [location.pathname]);

  // Auto-suggest
  useEffect(() => {
    const val = normalize(qText);
    if (val.length < 2) {
      setSuggests([]);
      setShowSuggest(false);
      setHighlight(-1);
      return;
    }

    let cancelled = false;
    const timeout = setTimeout(async () => {
      try {
        setLoadingSuggest(true);
        const COL = collection(db, "search_index");
        const TAKE = 8;

        const q1 = query(
          COL,
          orderBy("titleLower"),
          startAt(val),
          endAt(val + "\uf8ff"),
          fsLimit(TAKE)
        );
        const s1 = await getDocs(q1);
        const r1 = s1.docs.map((d) => ({ id: d.id, ...d.data() }));

        const tokens = val.split(" ").filter(Boolean);
        const last = tokens[tokens.length - 1] || "";
        let r2 = [];
        if (last.length >= 2) {
          const q2 = query(COL, where("tokens", "array-contains", last), fsLimit(TAKE));
          const s2 = await getDocs(q2);
          r2 = s2.docs.map((d) => ({ id: d.id, ...d.data() }));
        }

        const merged = uniqueByRefId([...r1, ...r2])
          .sort((a, b) => {
            const pa = Number(a.popularity || 0);
            const pb = Number(b.popularity || 0);
            if (pb !== pa) return pb - pa;
            return String(a.title || "").localeCompare(String(b.title || ""));
          })
          .slice(0, TAKE);

        if (!cancelled) {
          setSuggests(merged);
          setShowSuggest(true);
          setHighlight(-1);
        }
      } catch {
        if (!cancelled) {
          setSuggests([]);
          setShowSuggest(false);
          setHighlight(-1);
        }
      } finally {
        if (!cancelled) setLoadingSuggest(false);
      }
    }, 180);

    return () => {
      clearTimeout(timeout);
      cancelled = true;
    };
  }, [qText]);

  // click outside => close suggestions
  useEffect(() => {
    const onDocClick = (e) => {
      if (!suggestBoxRef.current || !inputRef.current) return;
      const inside =
        suggestBoxRef.current.contains(e.target) || inputRef.current.contains(e.target);
      if (!inside) setShowSuggest(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const onSearch = (e) => {
    e.preventDefault();
    const { params } = parseSmartQuery(qText);
    const url = `/catalogue${params.toString() ? `?${params.toString()}` : ""}`;
    navigate(url);
    setOpenLeft(false);
    setShowSuggest(false);
  };

  const clearSearch = () => {
    setQText("");
    setSuggests([]);
    setShowSuggest(false);
    setHighlight(-1);
    inputRef.current?.focus();
  };

  const onKeyDown = (e) => {
    if (!showSuggest || suggests.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => (h + 1) % suggests.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => (h <= 0 ? suggests.length - 1 : h - 1));
    } else if (e.key === "Enter" && highlight >= 0) {
      e.preventDefault();
      const it = suggests[highlight];
      if (it?.url) {
        navigate(it.url);
        setShowSuggest(false);
        setOpenLeft(false);
      }
    } else if (e.key === "Escape") {
      setShowSuggest(false);
    }
  };

  return (
    <>
      {/* ✅ Top modern bar */}
      <header className="ggf-topbar sticky-top">
        <div className="container d-flex align-items-center gap-2 py-2">
          {/* Left: burger */}
          <button
            className="ggf-burger d-lg-none"
            type="button"
            aria-label="Ouvrir le menu"
            onClick={() => setOpenLeft(true)}
          >
            <span />
            <span />
            <span />
          </button>

          {/* Logo */}
          <Link className="ggf-brand d-flex align-items-center gap-2" to="/" onClick={() => setOpenLeft(false)}>
            <img
              src="/favicon.png"
              alt="Logo GOD'S GIFT FASHION"
              className="d-inline-block d-md-none"
              width="44"
              height="44"
              loading="lazy"
              style={{ objectFit: "contain" }}
            />
            <img
              src="/logo192.png"
              alt="Logo GOD'S GIFT FASHION"
              className="d-none d-md-inline-block"
              width="110"
              height="auto"
              loading="lazy"
              style={{ objectFit: "contain" }}
            />
          </Link>

          {/* Search */}
          <div className="ggf-search ms-auto position-relative" ref={suggestBoxRef}>
            <form onSubmit={onSearch} role="search" aria-label="Recherche produits">
              <div className="input-group">
                <span className="input-group-text bg-white border-end-0">
                  <i className="bi bi-search" />
                </span>
                <input
                  ref={inputRef}
                  className="form-control border-start-0"
                  type="search"
                  placeholder="Ex: robe rouge M, basket 42, chemise <20000"
                  aria-label="Rechercher des produits"
                  value={qText}
                  onChange={(e) => setQText(e.target.value)}
                  onFocus={() => suggests.length > 0 && setShowSuggest(true)}
                  onKeyDown={onKeyDown}
                />
                {qText && (
                  <button
                    className="btn btn-outline-secondary"
                    type="button"
                    onClick={clearSearch}
                    aria-label="Effacer la recherche"
                  >
                    <i className="bi bi-x-lg" />
                  </button>
                )}
                <button className="btn btn-dark" type="submit" aria-label="Lancer la recherche">
                  <span className="d-inline d-md-none">
                    <i className="bi bi-search" />
                  </span>
                  <span className="d-none d-md-inline">
                    Rechercher <i className="bi bi-search ms-1" />
                  </span>
                </button>
              </div>
            </form>

            {/* Suggestions */}
            {showSuggest && (
              <div className="ggf-suggest" role="listbox" aria-label="Suggestions de recherche">
                {loadingSuggest && <div className="p-2 text-muted small">Recherche…</div>}
                {!loadingSuggest && suggests.length === 0 && (
                  <div className="p-2 text-muted small">Aucune suggestion</div>
                )}

                {!loadingSuggest &&
                  suggests.map((sug, idx) => (
                    <Link
                      key={sug.id || sug.refId || idx}
                      to={sug.url || "#"}
                      className={`ggf-suggest-item ${idx === highlight ? "active" : ""}`}
                      role="option"
                      onClick={() => {
                        setShowSuggest(false);
                        setOpenLeft(false);
                      }}
                    >
                      {sug.image ? (
                        <img
                          src={sug.image}
                          alt=""
                          width={40}
                          height={40}
                          style={{ objectFit: "cover", borderRadius: 10 }}
                          loading="lazy"
                        />
                      ) : (
                        <div className="bg-light" style={{ width: 40, height: 40, borderRadius: 10 }} />
                      )}

                      <div className="d-flex flex-column">
                        <span className="small fw-semibold">
                          {sug.title}
                          {sug.type ? <span className="ms-2 badge text-bg-secondary">{sug.type}</span> : null}
                        </span>
                        <span className="small text-muted">
                          {sug.type === "product"
                            ? "Voir le produit"
                            : sug.type === "category"
                            ? "Voir la catégorie"
                            : sug.type === "brand"
                            ? "Voir la marque"
                            : ""}
                        </span>
                      </div>
                    </Link>
                  ))}
              </div>
            )}
          </div>

          {/* Cart */}
          <Link
            to="/cart"
            className="ggf-cartbtn position-relative ms-2"
            aria-label="Voir le panier"
            title="Panier"
          >
            <i className="bi bi-bag fs-5" />
            {countCart > 0 && <span className="ggf-badge">{countCart}</span>}
          </Link>
        </div>
      </header>

      {/* ✅ Left drawer menu (mobile) */}
      <div className={`ggf-drawer ${openLeft ? "open" : ""}`} aria-hidden={!openLeft}>
        <div
          className="ggf-drawer-backdrop"
          onClick={() => setOpenLeft(false)}
          role="button"
          tabIndex={-1}
          aria-label="Fermer le menu"
        />
        <aside className="ggf-drawer-panel">
          <div className="ggf-drawer-head">
            <div className="d-flex align-items-center gap-2">
              
              <Link className="ggf-drawer-link" to="/" onClick={() => setOpenLeft(false)}>
                  <img src="/logo192.png" alt="Logo" width="92" height="auto" style={{ objectFit: "contain" }} />
              </Link>
            </div>
            <button className="btn btn-sm btn-outline-secondary" onClick={() => setOpenLeft(false)} type="button">
              <i className="bi bi-x-lg" />
            </button>
          </div>

          <div className="ggf-drawer-body">
            <div className="d-grid gap-2">
              <Link className="ggf-drawer-link" to="/cart" onClick={() => setOpenLeft(false)}>
                <i className="bi bi-bag" /> <span>Panier</span>
                {countCart > 0 ? <span className="ms-auto badge text-bg-danger">{countCart}</span> : null}
              </Link>
            </div>

            <hr className="my-3" />

            <div className="text-muted small">
              Astuce : cherche “robe rouge M”, “basket 42”, “chemise &lt;20000”.
            </div>
          </div>
        </aside>
      </div>

      {/* Optional floating bubble (mobile) */}
      <Link to="/cart" className="floating-cart-bubble d-lg-none" aria-label="Ouvrir le panier" title="Panier">
        <i className="bi bi-bag" />
        {countCart > 0 ? <span className="bubble-badge">{countCart}</span> : null}
      </Link>

      <style>{`
        /* ---------- Topbar modern ---------- */
        .ggf-topbar{
          background: rgba(255,255,255,.86);
          backdrop-filter: blur(10px);
          border-bottom: 1px solid rgba(0,0,0,.06);
          z-index: 1030;
        }
        .ggf-brand img{ display:block; }
        .ggf-search{ width: min(720px, 100%); }

        .ggf-suggest{
          position:absolute;
          top: calc(100% + 8px);
          left: 0;
          width: 100%;
          max-height: 360px;
          overflow: auto;
          background:#fff;
          border:1px solid rgba(0,0,0,.08);
          border-radius: 14px;
          box-shadow: 0 14px 30px rgba(0,0,0,.12);
          z-index: 1050;
        }
        .ggf-suggest-item{
          display:flex;
          align-items:center;
          gap:10px;
          padding:10px 12px;
          text-decoration:none;
          color:#0b1226;
        }
        .ggf-suggest-item:hover,
        .ggf-suggest-item.active{ background: rgba(0,0,0,.04); }

        .ggf-cartbtn{
          width: 44px;
          height: 44px;
          border-radius: 999px;
          display:grid;
          place-items:center;
          border: 1px solid rgba(0,0,0,.10);
          background:#fff;
          color:#0b1226;
          text-decoration:none;
        }
        .ggf-cartbtn:hover{ background: rgba(0,0,0,.03); color:#0b1226; }
        .ggf-badge{
          position:absolute;
          top:-6px;
          right:-6px;
          min-width: 20px;
          height: 20px;
          padding: 0 6px;
          border-radius: 999px;
          background:#dc3545;
          color:#fff;
          font-size: 12px;
          font-weight: 800;
          display:grid;
          place-items:center;
          border:2px solid #fff;
        }

        /* Burger */
        .ggf-burger{
          width: 44px;
          height: 44px;
          border-radius: 12px;
          border: 1px solid rgba(0,0,0,.10);
          background:#fff;
          display:flex;
          flex-direction:column;
          justify-content:center;
          gap:4px;
          padding: 0 12px;
        }
        .ggf-burger span{
          height:2px; width: 100%;
          background:#0b1226;
          border-radius: 2px;
        }

        /* ---------- Drawer left ---------- */
        .ggf-drawer{
          position: fixed;
          inset: 0;
          z-index: 1060;
          pointer-events: none;
          opacity: 0;
          transition: opacity .18s ease;
        }
        .ggf-drawer.open{
          opacity: 1;
          pointer-events: auto;
        }
        .ggf-drawer-backdrop{
          position:absolute;
          inset:0;
          background: rgba(0,0,0,.35);
        }
        .ggf-drawer-panel{
          position:absolute;
          top:0; left:0;
          height:100%;
          width: 86%;
          max-width: 360px;
          background:#fff;
          transform: translateX(-102%);
          transition: transform .22s ease;
          box-shadow: 18px 0 40px rgba(0,0,0,.18);
          display:flex;
          flex-direction:column;
        }
        .ggf-drawer.open .ggf-drawer-panel{
          transform: translateX(0);
        }
        .ggf-drawer-head{
          display:flex;
          align-items:center;
          justify-content:space-between;
          padding: 14px 14px;
          border-bottom:1px solid rgba(0,0,0,.06);
        }
        .ggf-drawer-body{
          padding: 12px 14px 18px;
          overflow:auto;
        }
        .ggf-drawer-link{
          display:flex;
          align-items:center;
          gap:10px;
          padding: 10px 12px;
          border-radius: 12px;
          border: 1px solid rgba(0,0,0,.07);
          text-decoration:none;
          color:#0b1226;
          font-weight:800;
          background:#fff;
        }
        .ggf-drawer-link:hover{ background: rgba(0,0,0,.03); color:#0b1226; }

        /* ---------- Floating bubble ---------- */
        .floating-cart-bubble{
          position: fixed;
          right: 16px;
          bottom: 16px;
          width: 56px;
          height: 56px;
          border-radius: 999px;
          display: grid;
          place-items: center;
          background: #198754;
          color: #fff;
          text-decoration: none;
          box-shadow: 0 14px 30px rgba(0,0,0,.18);
          z-index: 1050;
        }
        .floating-cart-bubble:hover{ color:#fff; filter: brightness(.95); transform: translateY(-1px); }
        .floating-cart-bubble i{ font-size: 22px; }
        .bubble-badge{
          position: absolute;
          top: -6px;
          right: -6px;
          min-width: 22px;
          height: 22px;
          padding: 0 6px;
          border-radius: 999px;
          background: #dc3545;
          color: #fff;
          font-size: 12px;
          font-weight: 800;
          display: grid;
          place-items: center;
          border: 2px solid #fff;
        }

        @media (min-width: 992px){
          .ggf-drawer{ display:none; }
        }
      `}</style>
    </>
  );
}

